/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Text,
  HStack,
  VStack,
  Box,
  Button,
  Select,
  FormLabel,
  Input,
  IconButton,
  Divider,
  useBoolean,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { add, format, getYear } from 'date-fns';
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { toTitleCase } from '../../utils/common.utils';

function NewDatePickerPopup(props) {
  const current = new Date();
  current.setMonth(current.getMonth() - 1);

  const { filterParams } = props;
  const { isComponentVisible, setIsComponentVisible, selectedValue } = props;
  const [isDateSelected, idsState] = useBoolean(false);
  const [selectedOption, setSelectedOption] = useState({});
  
  // Track if any changes were made
  const [hasChanges, setHasChanges] = useBoolean(false);
  
  // Initialize dateType
  const [dateType, setDateType] = useState('service_date');
  
  const selectedFilter = JSON.parse(localStorage.getItem('selectedFilter'));
  const selectedDates = JSON.parse(localStorage.getItem('customDateFilter'));

  const last_year_from_current_date = getYear(add(new Date(), {years: -1}));
  const sec_last_year_from_current_date = getYear(add(new Date(), {years: -2}));

  const drop_down_option = {
    'all':'All',
    'custom':'Custom dates',
    'week': 'This Week',
    'last_week': 'Last Week',
    'month': 'This month',
    'last_month': 'Last month',
    'quarter': 'This quarter',
    'last_quarter': 'Last quarter',
    'last_six_month': 'Last 6 months',
    'last_twelve_month': 'Last 12 months',
    'current_year': 'This Year',
    [`dynamic_year_${last_year_from_current_date}`]: last_year_from_current_date,
    [`dynamic_year_${sec_last_year_from_current_date}`]: sec_last_year_from_current_date,
  }

  const [toDate, setToDate] = useState([
    {
      startDate: filterParams.previous_start_date
        ? new Date(filterParams.previous_start_date)
        : new Date(),
      endDate: filterParams.previous_end_date
        ? new Date(filterParams.previous_end_date)
        : new Date(),
      key: 'selection',
    },
  ]);
  const [fromDate, setFromDate] = useState([
    {
      startDate: filterParams.start_date
        ? new Date(filterParams.start_date)
        : add(new Date(), { days: -30 }),
      endDate: filterParams.end_date
        ? new Date(filterParams.end_date)
        : new Date(),
      key: 'selection',
    },
  ]);

  // Reset to current filter values when popup opens
  useEffect(() => {
    if (isComponentVisible) {
      // Reset dateType to current filter value
      const currentDateType = filterParams.date_type || 
                             localStorage.getItem('dateType') || 
                             'service_date';
      setDateType(currentDateType);
      
      // Reset time period to current filter value
      if (selectedValue && drop_down_option.hasOwnProperty(selectedValue)) {
        setSelectedOption({
          label: drop_down_option[selectedValue],
          value: selectedValue,
        });
      } else if (selectedValue) {
        setSelectedOption({
          label: 'This month',
          value: 'month',
        });
      }
      
      // Reset dates to current filter values
      setFromDate([
        {
          startDate: filterParams.start_date
            ? new Date(filterParams.start_date)
            : add(new Date(), { days: -30 }),
          endDate: filterParams.end_date
            ? new Date(filterParams.end_date)
            : new Date(),
          key: 'selection',
        },
      ]);
      
      setToDate([
        {
          startDate: filterParams.previous_start_date
            ? new Date(filterParams.previous_start_date)
            : new Date(),
          endDate: filterParams.previous_end_date
            ? new Date(filterParams.previous_end_date)
            : new Date(),
          key: 'selection',
        },
      ]);
      
      // Reset changes flag
      setHasChanges.off();
    }
  }, [isComponentVisible]);

  useEffect(() => {
    if (
      selectedDates &&
      selectedFilter === 'custom' &&
      Object.keys(selectedDates)?.length
    ) {
      idsState.on();
      setFromDate([
        {
          startDate: new Date(filterParams.start_date),
          endDate: new Date(filterParams.end_date),
          key: 'selection',
        },
      ]);
      setToDate([
        {
          startDate: new Date(filterParams.previous_start_date),
          endDate: new Date(filterParams.previous_end_date),
          key: 'selection',
        },
      ]);
    }
  }, [filterParams]);

  const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display in US format (MM/DD/YYYY)
  const formatDateForDisplay = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  const averageDates = async (
    fromStartDate,
    fromEndDate,
    toStartDate,
    toEndDate
  ) => {
    try {
      // Convert dates from YYYY-MM-DD to MM-DD-YYYY and then to Date object (matches original)
      const formatDate = date => {
        let convertDate;
        const parts = date.split('-');

        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const parsedDate = new Date(year, month, day);

        if (!isNaN(parsedDate.getTime())) {
          const formattedDate = `${(parsedDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${parsedDate
              .getDate()
              .toString()
              .padStart(2, '0')}-${parsedDate.getFullYear()}`;
          convertDate = formattedDate;
        }
        return new Date(convertDate);
      };

      const selectedDates = {
        start_date: formatDate(toStartDate),
        end_date: formatDate(toEndDate),
        previous_start_date: formatDate(fromStartDate),
        previous_end_date: formatDate(fromEndDate),
        date_type: dateType,
      };
      await props.onSubmit({ ...selectedDates });
      window.localStorage.setItem(
        'customDateFilter',
        JSON.stringify(selectedDates)
      );
      // Store dateType in localStorage
      window.localStorage.setItem('dateType', dateType);
      setHasChanges.off();
    } catch (error) {
      console.error('Error applying dates:', error);
    } finally {
      if (props.onClose) {
        props.onClose();
      }
    }
  };

  // Handle dateType change
  const handleDateTypeChange = (e) => {
    const newDateType = e.target.value;
    setDateType(newDateType);
    setHasChanges.on();
  };

  // Handle time period change
  const handleTimePeriodChange = (e) => {
    const value = e.target.value;
    const label = drop_down_option[value];
    setSelectedOption({ label, value });
    setHasChanges.on();
    
    if (value === 'custom') {
      localStorage.setItem('selectedFilter', JSON.stringify('custom'));
    }
  };

  // Apply changes for non-custom selections
  const handleApplyChanges = () => {
    if (selectedOption.value !== 'custom') {
      // Save to localStorage
      window.localStorage.setItem('dateType', dateType);
      
      props.onSelectClick({ 
        label: selectedOption.label, 
        value: selectedOption.value, 
        date_type: dateType 
      });
      setIsComponentVisible(false);
      setHasChanges.off();
    }
  };

  // Handle close button - just close without applying changes
  const handleClose = () => {
    // Don't save anything, just close
    // The useEffect will reset values when reopened
    props.onClose();
    setIsComponentVisible(false);
  };

  return isComponentVisible ? (
    <Box
      background={'white'}
      fontFamily={FONT_FAMILY.ptSans}
      zIndex={10}
      boxShadow={'lg'}
      borderRadius={'lg'}
      fontSize="sm"
      fontWeight={'normal'}
      position="absolute"
      right={{ base: '10px', md: '2px' }}
      top={{ base: '60px', md: '50px' }}
      border="1px solid"
      borderColor="gray.200"
      w={{ base: '95vw', sm: '90vw', md: '420px' }}
      maxW="420px"
      maxH={{ base: '85vh', md: 'auto' }}
      overflowY="auto"
    >
      {/* Close Button */}
      <Box position="absolute" top={2} right={2} zIndex={1}>
        <IconButton
          icon={<CloseIcon />}
          size="sm"
          variant="ghost"
          onClick={handleClose}
          aria-label="Close"
        />
      </Box>

      <VStack spacing={4} p={{ base: 4, md: 5 }} align="stretch">
        {/* Date Type Selector */}
        <Box>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1.5}>
            Date Type
          </FormLabel>
          <Select
            value={dateType}
            onChange={handleDateTypeChange}
            bg="white"
            borderColor={AppColors.secondary}
            borderRadius="md"
            fontWeight="bold"
            fontSize="sm"
            h="44px"
            color={AppColors.secondary}
            fontFamily={FONT_FAMILY.ptSans}
            _hover={{ borderColor: AppColors.primary }}
            _focus={{ 
              borderColor: AppColors.primary, 
              boxShadow: 'none',
              outline: 'none'
            }}
            sx={{
              option: {
                fontFamily: FONT_FAMILY.ptSans,
                fontSize: 'sm',
                fontWeight: '600'
              }
            }}
          >
            <option value="service_date">Service Date</option>
            <option value="upload_date">Upload Date</option>
            <option value="archived_date">Completed Date</option>
          </Select>
        </Box>

        <Divider borderColor="gray.200" />

        {/* Time Period Selector */}
        <Box>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1.5}>
            Time Period
          </FormLabel>
          <Select
            value={selectedOption.value}
            onChange={handleTimePeriodChange}
            bg="white"
            borderColor={AppColors.secondary}
            borderRadius="md"
            fontWeight="bold"
            fontSize="sm"
            h="44px"
            color={AppColors.secondary}
            fontFamily={FONT_FAMILY.ptSans}
            _hover={{ borderColor: AppColors.primary }}
            _focus={{ 
              borderColor: AppColors.primary, 
              boxShadow: 'none',
              outline: 'none'
            }}
            sx={{
              option: {
                fontFamily: FONT_FAMILY.ptSans,
                fontSize: 'sm',
                fontWeight: '600'
              }
            }}
          >
            <option value="all">All</option>
            <option value="custom">Custom dates</option>
            <option value="week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="quarter">This Quarter</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="last_six_month">Last 6 Months</option>
            <option value="last_twelve_month">Last 12 Months</option>
            <option value="current_year">This Year</option>
            <option value={`dynamic_year_${last_year_from_current_date}`}>{last_year_from_current_date}</option>
            <option value={`dynamic_year_${sec_last_year_from_current_date}`}>{sec_last_year_from_current_date}</option>
          </Select>
        </Box>

        {/* Apply Button for non-custom selections - only show if changes were made */}
        {selectedOption.value !== 'custom' && hasChanges && (
          <>
            <Divider borderColor="gray.200" />
            <Button
              bg={AppColors.primary}
              color="white"
              onClick={handleApplyChanges}
              isLoading={props.isLoading}
              loadingText="Applying"
              borderRadius="md"
              fontWeight="600"
              fontSize="sm"
              h="44px"
              _hover={{ bg: AppColors.primary, opacity: 0.9 }}
            >
              Apply Filter
            </Button>
          </>
        )}

        {/* Custom Date Selection */}
        {selectedOption.value === 'custom' && (
          <VStack spacing={3} align="stretch" pt={2}>
            <Divider borderColor="gray.200" />
            
            {/* Recent Dates */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="700" color="gray.700">
                  Recent Period
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  {formatDateForDisplay(fromDate[0].startDate)} - {formatDateForDisplay(fromDate[0].endDate)}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Box flex={1}>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>From</FormLabel>
                  <Input
                    type="date"
                    value={formatDateForInput(fromDate[0].startDate)}
                    onChange={(e) => {
                      idsState.on();
                      setFromDate([{
                        ...fromDate[0],
                        startDate: new Date(e.target.value)
                      }]);
                    }}
                    max={formatDateForInput(new Date())}
                    bg="white"
                    borderColor="grey"
                    borderRadius="md"
                    fontSize="sm"
                    h="40px"
                    _hover={{ borderColor: AppColors.primary }}
                    _focus={{ 
                      borderColor: AppColors.primary, 
                      boxShadow: 'none'
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>To</FormLabel>
                  <Input
                    type="date"
                    value={formatDateForInput(fromDate[0].endDate)}
                    onChange={(e) => {
                      idsState.on();
                      setFromDate([{
                        ...fromDate[0],
                        endDate: new Date(e.target.value)
                      }]);
                    }}
                    max={formatDateForInput(new Date())}
                    bg="white"
                    borderColor="grey"
                    borderRadius="md"
                    fontSize="sm"
                    h="40px"
                    _hover={{ borderColor: AppColors.primary }}
                    _focus={{ 
                      borderColor: AppColors.primary, 
                      boxShadow: 'none'
                    }}
                  />
                </Box>
              </HStack>
            </Box>

            {/* Compare Period */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="700" color="gray.700">
                  Compare Period
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  {formatDateForDisplay(toDate[0].startDate)} - {formatDateForDisplay(toDate[0].endDate)}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Box flex={1}>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>From</FormLabel>
                  <Input
                    type="date"
                    value={formatDateForInput(toDate[0].startDate)}
                    onChange={(e) => {
                      idsState.on();
                      setToDate([{
                        ...toDate[0],
                        startDate: new Date(e.target.value)
                      }]);
                    }}
                    max={formatDateForInput(new Date())}
                    bg="white"
                    borderColor="grey"
                    borderRadius="md"
                    fontSize="sm"
                    h="40px"
                    _hover={{ borderColor: AppColors.secondary }}
                    _focus={{ 
                      borderColor: AppColors.secondary, 
                      boxShadow: 'none'
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <FormLabel fontSize="xs" color="gray.600" mb={1}>To</FormLabel>
                  <Input
                    type="date"
                    value={formatDateForInput(toDate[0].endDate)}
                    onChange={(e) => {
                      idsState.on();
                      setToDate([{
                        ...toDate[0],
                        endDate: new Date(e.target.value)
                      }]);
                    }}
                    max={formatDateForInput(new Date())}
                    bg="white"
                    borderColor="grey"
                    borderRadius="md"
                    fontSize="sm"
                    h="40px"
                    _hover={{ borderColor: AppColors.secondary }}
                    _focus={{ 
                      borderColor: AppColors.secondary, 
                      boxShadow: 'none'
                    }}
                  />
                </Box>
              </HStack>
            </Box>

            {/* Action Buttons */}
            <HStack spacing={3} pt={3}>
              <Button
                flex={1}
                variant="outline"
                onClick={handleClose}
                borderRadius="md"
                fontWeight="600"
                fontSize="sm"
                h="44px"
              >
                Cancel
              </Button>
              <Button
                flex={1}
                bg={AppColors.primary}
                color="white"
                onClick={() => {
                  averageDates(
                    format(toDate[0].startDate, 'yyyy-MM-dd'),
                    format(toDate[0].endDate, 'yyyy-MM-dd'),
                    format(fromDate[0].startDate, 'yyyy-MM-dd'),
                    format(fromDate[0].endDate, 'yyyy-MM-dd')
                  );
                  setIsComponentVisible(false);
                }}
                isLoading={props.isLoading}
                loadingText="Applying"
                borderRadius="md"
                fontWeight="600"
                fontSize="sm"
                h="44px"
                _hover={{ bg: AppColors.primary, opacity: 0.9 }}
              >
                Apply
              </Button>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Box>
  ) : null;
}

export default NewDatePickerPopup;